/**
 * FreeformBuildModule — the Build tab + part rendering (P14.4).
 *
 * Roblox/Second-Life freeform building for Personal/Individual worlds: a prim
 * palette that rezzes parts, a Land-Impact HUD (li_used/li_max), a parts list with
 * delete + link/unlink, and a "New Individual World" action — plus rendering each
 * replicated part as a primitive mesh in the scene. The client RENDERS + CALLS
 * reducers; every Land-Impact value and linkset rule is server-authoritative.
 */

import * as THREE from 'three/webgpu';
import type { App, IModule } from 'enginex';
import type { DbConnection } from '../module_bindings/index.js';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { REZ_PRIM_TYPES } from '@shared/types/freeform.js';
import { notifyPartInsert } from '../lib/build_commands.js';
import { canBuildIn } from '../lib/world_caps.js';
import { ASSET_STORE_BASE } from '../config.js';
import type { AssetModule } from './AssetModule.js';
import type { PhysicsModule } from './PhysicsModule.js';
import type { ColliderProxy } from '../lib/collider_proxy.js';
import { seatingOffset, pivotBodyPos, type Vec3Like } from '../lib/structure_pivot.js';
import { iconMarkup } from '../lib/icons.js';

export class FreeformBuildModule implements IModule {
  readonly name = 'FreeformBuild';
  private scene!: THREE.Scene;
  private bound = false;
  private els: Record<string, HTMLElement> = {};
  private partRoot = new THREE.Group();
  /** The group holding every rendered part — PersonalBuildGizmoModule raycasts it. */
  get root(): THREE.Group { return this.partRoot; }
  /** The rendered mesh for a part id — world scripts reference/animate parts through this. */
  partMesh(partId: string): THREE.Object3D | undefined { return this.partMeshes.get(partId); }
  /** Interior shells: hide the part's render + static collider while the
   *  player is inside; restore re-seats the collider on the next render pass. */
  setPartHidden(partId: string, hidden: boolean): void {
    const mesh = this.partMeshes.get(partId);
    if (mesh) mesh.visible = !hidden;
    if (hidden) {
      this.physics.removeStaticCollider(`part:${partId}`);
      this.collided.delete(partId);
      this.colliderSig.delete(partId);
    } else {
      const conn = atlas.conn;
      const target = conn ? this.buildable(conn) : null;
      if (conn && target) this.renderMeshes(conn, target);
    }
  }
  private partMeshes = new Map<string, THREE.Object3D>();
  private catalogAssets = new Map<string, bigint>(); // G1: partId → catalog/UGC render_asset
  private collided = new Set<string>(); // partId keys with a live static collider
  private colliderSig = new Map<string, string>(); // partId → transform signature of the seated collider
  private readonly proxyCache = new Map<string, ColliderProxy>(); // assetId → fetched collider proxy
  private readonly pivotOffsets = new Map<string, Vec3Like>(); // partId → structure seating offset (natural scale; lib/structure_pivot)
  private mat = new THREE.MeshStandardMaterial({ color: 0x9ec5ff, roughness: 0.5 });
  private rezN = 0;

  constructor(private readonly assetModule: AssetModule, private readonly physics: PhysicsModule) {}

  init(app: App): void {
    this.scene = app.world.scene;
    this.scene.add(this.partRoot);
    for (const id of ['ff-status', 'ff-palette', 'ff-budget', 'ff-parts', 'ff-note']) {
      const el = document.getElementById(id);
      if (el) this.els[id] = el;
    }
    bus.on('atlas:account-ready', () => {
      this.bind();
      this.render();
    });
    for (const ev of ['atlas:freeform-updated', 'atlas:world-entered'] as const) {
      bus.on(ev, () => this.render());
    }
    this.render();
  }

  private bind(): void {
    if (this.bound) return;
    const conn = atlas.conn;
    if (!conn) return;
    this.bound = true;
    // Trailing debounce: every replicated part row re-rendered the whole parts
    // list (innerHTML) — O(parts²) at join with 256-part linksets (2026-09-12).
    let timer: number | null = null;
    const ping = (): void => {
      if (timer !== null) return;
      timer = window.setTimeout(() => { timer = null; bus.emit('atlas:freeform-updated', {}); }, 150);
    };
    for (const tbl of [conn.db.part, conn.db.linkset, conn.db.worldBuildBudget]) {
      const t = tbl as unknown as { onInsert(cb: () => void): void; onUpdate(cb: () => void): void; onDelete(cb: () => void): void };
      t.onInsert(ping);
      t.onUpdate(ping);
      t.onDelete(ping);
    }
    // Build Mode v2 rez correlator: a rez reducer has no return value, so the
    // command stack binds the new partId from the insert stream.
    (conn.db.part as unknown as { onInsert(cb: (c: unknown, row: { partId: bigint }) => void): void })
      .onInsert((_c, row) => notifyPartInsert(row.partId));
  }

  /** The current world's id + whether it is freeform-buildable by the player. */
  private buildable(conn: DbConnection): { worldId: bigint; freeform: boolean } | null {
    const wid = atlas.currentWorldId;
    if (wid === null) return null;
    const world = conn.db.world.worldId.find(wid);
    if (!world) return null;
    // Only worlds the player may actually BUILD in show the building UI (owner,
    // admin, or a build grant) — the server re-checks on every rez/edit anyway.
    return { worldId: wid, freeform: world.kind !== 'commons' && canBuildIn(wid) };
  }

  private geoFor(primType: string): THREE.BufferGeometry {
    switch (primType) {
      case 'sphere':
        return new THREE.SphereGeometry(0.5, 16, 12);
      case 'cylinder':
        return new THREE.CylinderGeometry(0.5, 0.5, 1, 16);
      case 'cone':
        return new THREE.ConeGeometry(0.5, 1, 16);
      case 'torus':
        return new THREE.TorusGeometry(0.5, 0.2, 12, 20);
      default:
        return new THREE.BoxGeometry(1, 1, 1);
    }
  }

  private render(): void {
    const conn = atlas.conn;
    if (!conn || !conn.identity) return;
    const target = this.buildable(conn);
    this.renderStatus(conn, target);
    this.renderBudget(conn, target);
    this.renderParts(conn, target);
    this.renderMeshes(conn, target);
  }

  private renderStatus(conn: DbConnection, target: ReturnType<FreeformBuildModule['buildable']>): void {
    const el = this.els['ff-status'];
    const palette = this.els['ff-palette'];
    if (!el) return;
    if (!target) {
      el.innerHTML = '<div class="empty">Enter a world to build.</div>';
      if (palette) palette.innerHTML = '';
    } else if (!target.freeform) {
      el.innerHTML = '<div class="meta">The Commons is deed-only. Switch to your Personal or an Individual world to build freeform.</div>' +
        '<button class="action" id="ff-new-btn">New Individual World</button>';
      if (palette) palette.innerHTML = '';
    } else {
      el.innerHTML = `<div class="meta">Building in world #${target.worldId} (freeform).</div>` +
        '<button class="action secondary" id="ff-new-btn">New Individual World</button>';
      if (palette) {
        palette.innerHTML = REZ_PRIM_TYPES.map((p) => `<button class="action" data-rez="${p}">${p}</button>`).join(' ');
        for (const btn of Array.from(palette.querySelectorAll('[data-rez]'))) {
          btn.addEventListener('click', () => this.rez(target.worldId, (btn as HTMLElement).dataset.rez!));
        }
      }
    }
    el.querySelector('#ff-new-btn')?.addEventListener('click', () => this.newIndividualWorld());
  }

  private renderBudget(conn: DbConnection, target: ReturnType<FreeformBuildModule['buildable']>): void {
    const el = this.els['ff-budget'];
    if (!el) return;
    if (!target || !target.freeform) {
      el.innerHTML = '';
      return;
    }
    const b = conn.db.worldBuildBudget.worldId.find(target.worldId);
    const used = b ? b.liUsed : 0;
    const max = b ? b.liMax : 750;
    const pct = max > 0 ? Math.round((used / max) * 100) : 0;
    el.innerHTML =
      `<div class="meta">${iconMarkup('brick')}Land Impact: <b>${used}/${max}</b></div>` +
      `<div style="height:8px;background:var(--ax-bg3);border-radius:var(--ax-radius);overflow:hidden"><div style="height:100%;width:${pct}%;background:${pct > 90 ? 'var(--ax-accent)' : 'var(--ax-accent)'}"></div></div>`;
  }

  private renderParts(conn: DbConnection, target: ReturnType<FreeformBuildModule['buildable']>): void {
    const el = this.els['ff-parts'];
    if (!el) return;
    if (!target || !target.freeform) {
      el.innerHTML = '';
      return;
    }
    const parts = [...conn.db.part.iter()].filter((p) => p.worldId === target.worldId);
    if (parts.length === 0) {
      el.innerHTML = '<div class="empty">No parts yet — rez a prim above.</div>';
      return;
    }
    const unlinked = parts.filter((p) => p.linksetId === 0n).length;
    el.innerHTML =
      parts
        .map(
          (p) =>
            `<div class="market-item"><div class="head"><span class="kind">${this.esc(p.primType)} #${p.partId}${p.linksetId !== 0n ? ` ${iconMarkup('logic')}` : ''}</span><span class="price">LI ${p.landImpact}</span></div>` +
            `<button class="action secondary" data-del="${p.partId.toString()}">Delete</button></div>`,
        )
        .join('') +
      (unlinked >= 2 ? '<button class="action" id="ff-link-btn">Link all unlinked</button>' : '');

    for (const btn of Array.from(el.querySelectorAll('[data-del]'))) {
      btn.addEventListener('click', () => this.del(BigInt((btn as HTMLElement).dataset.del!)));
    }
    el.querySelector('#ff-link-btn')?.addEventListener('click', () => this.linkUnlinked(target.worldId));
  }

  /** Render each replicated part — a primitive prim, or (G1) a first-party catalog model. */
  private renderMeshes(conn: DbConnection, target: ReturnType<FreeformBuildModule['buildable']>): void {
    const live = new Set<string>();
    if (target) {
      for (const p of conn.db.part.iter()) {
        if (p.worldId !== target.worldId) continue;
        const key = p.partId.toString();
        live.add(key);
        const xf = { posX: p.posX, posY: p.posY, posZ: p.posZ, rotX: p.rotX, rotY: p.rotY, rotZ: p.rotZ, rotW: p.rotW, scaleX: p.scaleX, scaleY: p.scaleY, scaleZ: p.scaleZ };
        // The gizmo owns the held mesh's transform until its commit round-trips.
        const held = atlas.gizmoHeldPartId === key;
        if (p.modelAssetId > 0n) {
          // G1 catalog prop OR G6 UGC model (rezCatalogPart / rezUploadedPart) → load the real GLB.
          if (this.catalogAssets.get(key) !== p.modelAssetId) {
            this.removePart(key);
            this.catalogAssets.set(key, p.modelAssetId);
            void this.placeCatalogPart(key, p.modelAssetId, xf, p.physics, p.meshRef);
          } else {
            const m = this.partMeshes.get(key); if (m && !held) this.applyXf(m, xf);
            // Re-seat the static collider when a physics part MOVED (fixes the
            // "collider stays behind after a gizmo edit" limitation): compare a
            // transform signature so unmoved parts cost nothing.
            if (!held && p.physics && this.collided.has(key)) {
              const sig = this.xfSig(xf);
              if (this.colliderSig.get(key) !== sig) {
                this.physics.removeStaticCollider(`part:${key}`);
                this.collided.delete(key);
                void this.addPartCollider(key, p.modelAssetId, xf);
              }
            }
          }
          continue;
        }
        let mesh = this.partMeshes.get(key);
        if (!mesh) {
          mesh = new THREE.Mesh(this.geoFor(p.primType), this.mat);
          mesh.userData.ffPartId = key; // gizmo pick target
          this.partRoot.add(mesh);
          this.partMeshes.set(key, mesh);
        }
        if (!held) this.applyXf(mesh, xf);
      }
    }
    for (const key of [...this.partMeshes.keys()]) if (!live.has(key)) this.removePart(key);
  }

  private applyXf(o: THREE.Object3D, p: { posX: number; posY: number; posZ: number; rotX: number; rotY: number; rotZ: number; rotW: number; scaleX: number; scaleY: number; scaleZ: number }): void {
    o.position.set(p.posX, p.posY, p.posZ);
    o.quaternion.set(p.rotX, p.rotY, p.rotZ, p.rotW);
    o.scale.set(p.scaleX, p.scaleY, p.scaleZ);
  }

  private async placeCatalogPart(key: string, assetId: bigint, xf: Parameters<FreeformBuildModule['applyXf']>[1], withPhysics: boolean, meshRef = ''): Promise<void> {
    const tmpl = await this.assetModule.load(assetId);
    if (this.catalogAssets.get(key) !== assetId) { this.assetModule.dispose(assetId); return; } // changed/removed mid-load
    let inst: THREE.Object3D = tmpl.clone();
    // Structure seating law (lib/structure_pivot.ts): the row is the FOOTPRINT
    // point; an off-pivot GLB hangs inside a holder offset by its natural bbox
    // bottom-center, which the row's rotation/scale then transform. Null for
    // well-pivoted models — they render exactly as before.
    const off = meshRef ? seatingOffset(meshRef, new THREE.Box3().setFromObject(tmpl)) : null;
    if (off) {
      const holder = new THREE.Group(); holder.position.set(off.x, off.y, off.z); holder.add(inst);
      const root = new THREE.Group(); root.add(holder);
      inst = root;
      this.pivotOffsets.set(key, off);
    } else this.pivotOffsets.delete(key);
    inst.userData.ffPartId = key; // gizmo pick target (group root carries the key)
    this.applyXf(inst, xf);
    this.partRoot.add(inst);
    this.partMeshes.set(key, inst);
    // G6: a physics-enabled catalog/UGC part gets a static collider from its render_asset's proxy.
    if (withPhysics) void this.addPartCollider(key, assetId, xf);
  }

  /** Build a static collider for a mesh part from its render_asset's collider proxy (reuses the G1 path). */
  private async addPartCollider(key: string, assetId: bigint, xf: Parameters<FreeformBuildModule['applyXf']>[1]): Promise<void> {
    const conn = atlas.conn;
    if (!conn) return;
    const col = conn.db.assetCollider.assetId.find(assetId);
    if (!col) return;
    let proxy = this.proxyCache.get(assetId.toString());
    if (!proxy) {
      try { proxy = (await (await fetch(`${ASSET_STORE_BASE}/${col.colliderUri}`)).json()) as ColliderProxy; this.proxyCache.set(assetId.toString(), proxy); }
      catch { return; }
    }
    if (this.catalogAssets.get(key) !== assetId) return; // changed/removed mid-fetch
    // Yaw from the quaternion + a uniform scale (the static-collider transform supports Y-yaw + uniform scale).
    const yaw = new THREE.Euler().setFromQuaternion(new THREE.Quaternion(xf.rotX, xf.rotY, xf.rotZ, xf.rotW), 'YXZ').y;
    // A seated structure's mesh-space proxy rides the same footprint offset (yaw-rotated, scaled).
    const off = this.pivotOffsets.get(key);
    const body = off ? pivotBodyPos({ x: xf.posX, y: xf.posY, z: xf.posZ }, yaw, xf.scaleX, off) : { x: xf.posX, y: xf.posY, z: xf.posZ };
    this.physics.addStaticCollider(`part:${key}`, proxy, { ...body, yaw, scale: xf.scaleX });
    this.collided.add(key);
    this.colliderSig.set(key, this.xfSig(xf));
  }

  /** Compact transform signature — collider re-seat detector. */
  private xfSig(xf: { posX: number; posY: number; posZ: number; rotY: number; rotW: number; scaleX: number }): string {
    return `${xf.posX.toFixed(3)},${xf.posY.toFixed(3)},${xf.posZ.toFixed(3)},${xf.rotY.toFixed(4)},${xf.rotW.toFixed(4)},${xf.scaleX.toFixed(3)}`;
  }

  private removePart(key: string): void {
    const mesh = this.partMeshes.get(key);
    if (mesh) {
      this.partRoot.remove(mesh);
      const assetId = this.catalogAssets.get(key);
      if (assetId !== undefined) this.assetModule.dispose(assetId);
      else if (mesh instanceof THREE.Mesh) mesh.geometry.dispose();
    }
    if (this.collided.has(key)) { this.physics.removeStaticCollider(`part:${key}`); this.collided.delete(key); }
    this.colliderSig.delete(key);
    this.pivotOffsets.delete(key);
    this.partMeshes.delete(key);
    this.catalogAssets.delete(key);
  }

  // ─── Actions ─────────────────────────────────────────────────────────────────

  private rez(worldId: bigint, primType: string): void {
    const conn = atlas.conn;
    if (!conn) return;
    const x = (this.rezN++ % 10) * 1.5 - 6;
    void conn.reducers
      .rezPart({ worldId, primType, posX: x, posY: 1, posZ: 0, rotX: 0, rotY: 0, rotZ: 0, rotW: 1, scaleX: 1, scaleY: 1, scaleZ: 1, shapeJson: '{}', materialJson: '{}', physics: false })
      .then(() => this.note(`Rezzed a ${primType}.`, 'ok'))
      .catch((e: Error) => this.note(e.message, 'err'));
  }

  private del(partId: bigint): void {
    const conn = atlas.conn;
    if (!conn) return;
    void conn.reducers.deletePart({ partId }).then(() => this.note('Part deleted.', 'ok')).catch((e: Error) => this.note(e.message, 'err'));
  }

  private linkUnlinked(worldId: bigint): void {
    const conn = atlas.conn;
    if (!conn) return;
    const ids = [...conn.db.part.iter()].filter((p) => p.worldId === worldId && p.linksetId === 0n).map((p) => p.partId);
    if (ids.length < 2) return;
    void conn.reducers.linkParts({ worldId, partIds: ids }).then(() => this.note('Linked into one object.', 'ok')).catch((e: Error) => this.note(e.message, 'err'));
  }

  private newIndividualWorld(): void {
    const conn = atlas.conn;
    if (!conn) return;
    this.note('Spawning a new Individual world…', '');
    void conn.reducers
      .spawnWorld({ name: `My World ${Date.now() % 100000}`, sizeM: 64, kind: 'individual', lifecycle: 'persistent', governanceTemplate: 'solo', capacity: 10 })
      .then(() => this.note('Individual world created — open it from your worlds.', 'ok'))
      .catch((e: Error) => this.note(e.message, 'err'));
  }

  private note(text: string, cls: '' | 'ok' | 'err'): void {
    const el = this.els['ff-note'];
    if (!el) return;
    el.className = cls ? `note ${cls}` : 'note';
    el.textContent = text;
  }

  private esc(s: string): string {
    return s.replace(/[&<>"']/g, (c) => (c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : c === '"' ? '&quot;' : '&#39;'));
  }

  dispose(): void {
    for (const [key, m] of this.partMeshes) {
      const assetId = this.catalogAssets.get(key);
      if (assetId !== undefined) this.assetModule.dispose(assetId);
      else if (m instanceof THREE.Mesh) m.geometry.dispose();
    }
    this.mat.dispose();
  }
}
